import React, { useCallback, useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import profileStyles from "../../../styles/pages/profile/ProfilePage.module.css";
import styles from "../../../styles/pages/profile/MatchesView.module.css";
import {
  FaThumbsUp,
  FaClock,
  FaTag,
  FaCalendarAlt,
  FaSearch,
  FaExclamationTriangle,
  FaMobileAlt,
  FaTrash,
  FaCheck,
} from "react-icons/fa";
import getImageUrl from "../../../utils/imageUtils";
import PublicPageLink from "../../common/PublicPageLink";
import api from "../../../utils/api/axiosInstance";
import ConfirmModal from "../../common/ConfirmModal";
import MatchesViewSkeleton from "./MatchesViewSkeleton";
import { capMatchPercent } from "../../../utils/matchScoreUtils";
import SessionTopMatchScore from "../SessionTopMatchScore";
import ShoppingProfileSuggestionBanner from "../ShoppingProfileSuggestionBanner";

const MAX_VISIBLE_SESSIONS = 25;

const getSessionId = (session) => session?._id || session?.id;

const MatchesView = ({
  matchSessions,
  loading,
  error,
  onRefresh,
  onDelete,
  currentTab,
}) => {
  const [identityReady, setIdentityReady] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pendingIds, setPendingIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/users/me/shopping-identity")
      .then((res) => {
        if (!cancelled) {
          setIdentityReady(Boolean(res.data?.completeness?.isReady));
        }
      })
      .catch(() => {
        if (!cancelled) setIdentityReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCategoryName = (session) => {
    if (session.matchProfile?.category?.name) {
      return session.matchProfile.category.name;
    }
    if (session.category?.name) return session.category.name;
    if (session.wizardAnswers?.type) return session.wizardAnswers.type;
    if (session.source === "feed_chat") return "Chat";
    return "Products";
  };

  const getSessionTitle = (session) => {
    if (session.source === "feed_chat") {
      const summary = session.chatSummary?.trim();
      if (summary && summary.length > 3 && !/^got it\.?$/i.test(summary)) {
        return summary.slice(0, 72);
      }
      return `${getCategoryName(session)} match`;
    }
    return `${getCategoryName(session)} search`;
  };

  const getTopMatch = (session) => session.matchedProducts?.[0] || null;

  const getTopExplanation = (session) => {
    const top = getTopMatch(session);
    const text = top?.matchExplanation;
    return typeof text === "string" && text.trim() ? text.trim() : null;
  };

  const trackInteraction = useCallback(async (action, session) => {
    const topMatch = getTopMatch(session);
    const productId =
      topMatch?.product?._id ||
      topMatch?.product ||
      topMatch?._id ||
      null;
    if (!session?.sessionId || !productId) return;
    try {
      await api.post("/analytics/interaction", {
        sessionId: session.sessionId,
        productId,
        action,
      });
    } catch (err) {
      console.warn("Failed to track interaction:", err);
    }
  }, []);

  const sortedSessions = useMemo(() => {
    const list = (matchSessions || []).filter(
      (s) => (s.matchedProducts?.length || 0) > 0,
    );
    list.sort((a, b) => {
      const aChat = a.source === "feed_chat" ? 1 : 0;
      const bChat = b.source === "feed_chat" ? 1 : 0;
      if (bChat !== aChat) return bChat - aChat;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    return list;
  }, [matchSessions]);

  const visibleSessions = useMemo(
    () => sortedSessions.slice(0, MAX_VISIBLE_SESSIONS),
    [sortedSessions],
  );
  const hiddenCount = Math.max(0, sortedSessions.length - MAX_VISIBLE_SESSIONS);
  const selectableIds = useMemo(
    () => visibleSessions.map((session) => getSessionId(session)).filter(Boolean),
    [visibleSessions],
  );
  const allVisibleSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const getSessionHref = (session) => {
    if (session.source === "feed_chat" && session.sessionId) {
      return `/community?tab=matches&matchSession=${encodeURIComponent(session.sessionId)}`;
    }
    return "/search";
  };

  const getSessionLinkState = (session) => {
    if (session.source === "feed_chat") return undefined;
    return {
      results: (session.matchedProducts || []).map((mp) => ({
        ...mp.product,
        matchScore: mp.matchScore || mp.score,
        matchExplanation: mp.matchExplanation,
      })),
      fromProfileTab: currentTab,
      sessionId: session.sessionId,
      wizardAnswers: session.wizardAnswers,
      isWizardResults: true,
    };
  };

  const toggleSelected = useCallback((id) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(allVisibleSelected ? [] : selectableIds);
  }, [allVisibleSelected, selectableIds]);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds([]);
  }, []);

  const handleRemoveClick = useCallback((e, session) => {
    e.preventDefault();
    e.stopPropagation();
    const id = getSessionId(session);
    if (!id || deleting) return;
    setPendingIds([id]);
    setShowDeleteModal(true);
  }, [deleting]);

  const handleBulkRemoveClick = useCallback(() => {
    if (selectedIds.length === 0 || deleting) return;
    setPendingIds(selectedIds);
    setShowDeleteModal(true);
  }, [selectedIds, deleting]);

  const confirmRemoveMatch = useCallback(async () => {
    if (pendingIds.length === 0 || !onDelete) return;
    const ids = pendingIds;
    const wasSelecting = selectMode;

    try {
      setDeleting(true);
      setShowDeleteModal(false);
      await onDelete(ids.length === 1 ? ids[0] : ids);
      if (wasSelecting) {
        setSelectedIds([]);
        setSelectMode(false);
      }
    } catch {
      // Parent shows error toast
    } finally {
      setDeleting(false);
      setPendingIds([]);
    }
  }, [pendingIds, onDelete, selectMode]);

  if (loading) {
    return <MatchesViewSkeleton />;
  }

  if (error) {
    return (
      <div className={`${profileStyles.contentSection} ${styles.page}`}>
        <div className={styles.errorState}>
          <FaExclamationTriangle size={40} />
          <h3>Unable to load matches</h3>
          <p>{error}</p>
          <button type="button" className="buttonPrimary" onClick={onRefresh}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${profileStyles.contentSection} ${styles.page}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>
            <FaThumbsUp className={styles.titleIcon} aria-hidden />
            My Matches
          </h2>
          <p className={styles.subtitle}>
            {matchSessions.length === 0
              ? "Saved results from chat and product searches"
              : `${matchSessions.length} saved ${
                  matchSessions.length === 1 ? "session" : "sessions"
                }`}
          </p>
        </div>
        <div className={styles.headerActions}>
          <PublicPageLink pageKey="feed" to="/community?tab=matches" className="buttonPrimary">
            <FaSearch aria-hidden /> Find products
          </PublicPageLink>
          {matchSessions.length > 0 && (
            <button
              type="button"
              className="buttonSecondary"
              onClick={onRefresh}
              title="Refresh list"
            >
              <FaClock aria-hidden /> Refresh
            </button>
          )}
          {onDelete && selectableIds.length > 0 && (
            <button
              type="button"
              className="buttonSecondary"
              onClick={() =>
                selectMode ? exitSelectMode() : setSelectMode(true)
              }
              aria-pressed={selectMode}
            >
              {selectMode ? (
                <>
                  <FaCheck aria-hidden /> Done
                </>
              ) : (
                "Select"
              )}
            </button>
          )}
        </div>
      </header>

      {selectMode && (
        <div className={styles.bulkBar} role="toolbar" aria-label="Bulk actions">
          <span className={styles.bulkCount}>
            {selectedIds.length} selected
          </span>
          <button
            type="button"
            className="buttonSecondary"
            onClick={handleSelectAll}
            disabled={deleting}
          >
            {allVisibleSelected ? "Clear" : hiddenCount > 0 ? "Select shown" : "Select all"}
          </button>
          <button
            type="button"
            className={`buttonSecondary ${styles.bulkDelete}`}
            onClick={handleBulkRemoveClick}
            disabled={selectedIds.length === 0 || deleting}
            aria-label={
              selectedIds.length > 0
                ? `Delete ${selectedIds.length} selected matches`
                : "Delete selected matches"
            }
          >
            <FaTrash aria-hidden />
            {deleting
              ? "Removing..."
              : selectedIds.length > 0
                ? `Delete (${selectedIds.length})`
                : "Delete"}
          </button>
        </div>
      )}

      {!identityReady && (
        <p className={styles.identityBanner}>
          Match scores are most trustworthy after you complete your{" "}
          <Link to="../settings">shopping profile</Link> in Settings.
        </p>
      )}

      {identityReady && <ShoppingProfileSuggestionBanner />}

      {matchSessions.length === 0 ? (
        <div className={styles.emptyState}>
          <FaThumbsUp className={styles.emptyIcon} size={48} aria-hidden />
          <h3>No matches yet</h3>
          <p>
            Chat on Home → My matches to get personalized picks. They will show
            up here with scores and explanations.
          </p>
          <PublicPageLink pageKey="feed" to="/community?tab=matches" className="buttonPrimary">
            Open My matches
          </PublicPageLink>
        </div>
      ) : (
        <>
          <div className={styles.sessionList}>
            {visibleSessions.map((session) => {
              const topMatch = getTopMatch(session);
              const topScore =
                topMatch?.matchScore ?? topMatch?.score ?? null;
              const topProductId =
                topMatch?.product?._id ||
                topMatch?.product ||
                topMatch?._id ||
                null;
              const topProducts = (session.matchedProducts || []).slice(0, 4);
              const explanation = getTopExplanation(session);
              const isFeedChat = session.source === "feed_chat";

              const sessionId = getSessionId(session);
              const isSelected = Boolean(sessionId && selectedIds.includes(sessionId));
              const isRemoving =
                deleting && Boolean(sessionId && pendingIds.includes(sessionId));
              const CardBody = selectMode ? "div" : Link;
              const cardProps = selectMode
                ? {
                    className: styles.sessionLink,
                    role: "button",
                    tabIndex: 0,
                    "aria-pressed": isSelected,
                    onClick: () => toggleSelected(sessionId),
                    onKeyDown: (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleSelected(sessionId);
                      }
                    },
                  }
                : {
                    to: getSessionHref(session),
                    state: getSessionLinkState(session),
                    className: styles.sessionLink,
                    onClick: () => trackInteraction("view", session),
                  };

              return (
                <article
                  key={session._id || session.sessionId}
                  className={`${styles.sessionCard} ${
                    isSelected ? styles.sessionCardSelected : ""
                  }`}
                >
                  <CardBody {...cardProps}>
                    <div className={styles.sessionTop}>
                      <div>
                        <h3 className={styles.sessionTitle}>
                          {getSessionTitle(session)}
                        </h3>
                        <div className={styles.sessionMeta}>
                          <span className={styles.metaChip}>
                            <FaTag aria-hidden /> {getCategoryName(session)}
                          </span>
                          {isFeedChat && (
                            <span className={`${styles.metaChip} ${styles.chatChip}`}>
                              Chat
                            </span>
                          )}
                          {session.createdAt && (
                            <span className={styles.metaChip}>
                              <FaCalendarAlt aria-hidden />{" "}
                              {formatDate(session.createdAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      {topScore != null && topProductId ? (
                        <SessionTopMatchScore
                          productId={topProductId}
                          score={topScore}
                        />
                      ) : topScore != null ? (
                        <span className={styles.scoreBadge}>
                          {capMatchPercent(topScore)}% top match
                        </span>
                      ) : null}
                    </div>

                    {explanation && (
                      <p className={styles.explanation}>
                        <span className={styles.explanationLabel}>
                          Why it fits you:{" "}
                        </span>
                        {explanation}
                      </p>
                    )}

                    {topProducts.length > 0 && (
                      <div className={styles.thumbRow}>
                        {topProducts.map((match, idx) => {
                          const product = match.product || match;
                          const score = match.matchScore || match.score;
                          return (
                            <div
                              key={product?._id || idx}
                              className={styles.thumb}
                            >
                              {product?.images?.[0] ? (
                                <img
                                  src={getImageUrl(product.images[0])}
                                  alt=""
                                />
                              ) : (
                                <div className={styles.thumbPlaceholder}>
                                  <FaMobileAlt aria-hidden />
                                </div>
                              )}
                              {score != null && (
                                <span className={styles.thumbScore}>
                                  {capMatchPercent(score)}%
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <p className={styles.openHint}>
                      {selectMode
                        ? isSelected
                          ? "Selected"
                          : "Click to select"
                        : isFeedChat
                          ? "Open on Home → My matches"
                          : "View full results →"}
                    </p>
                  </CardBody>
                  {selectMode && sessionId ? (
                    <div className={styles.cardFooter}>
                      <label className={styles.selectControl}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelected(sessionId)}
                          aria-label={`Select ${getSessionTitle(session)}`}
                          disabled={deleting}
                        />
                        Select
                      </label>
                    </div>
                  ) : (
                    onDelete &&
                    sessionId && (
                      <div className={styles.cardFooter}>
                        <button
                          type="button"
                          className={`${profileStyles.actionButton} ${profileStyles.reviewDeleteButton}`}
                          onClick={(e) => handleRemoveClick(e, session)}
                          disabled={isRemoving}
                          title="Remove match"
                          aria-label="Remove match"
                        >
                          <FaTrash aria-hidden />
                          {isRemoving ? "Removing..." : "Remove"}
                        </button>
                      </div>
                    )
                  )}
                </article>
              );
            })}
          </div>

          {hiddenCount > 0 && (
            <p className={styles.moreNote}>
              Showing {MAX_VISIBLE_SESSIONS} of {sortedSessions.length} sessions.
            </p>
          )}
        </>
      )}

      {showDeleteModal && (
        <ConfirmModal
          message={
            pendingIds.length > 1
              ? `Remove ${pendingIds.length} matches from your saved sessions?`
              : "Remove this match from your saved sessions?"
          }
          onConfirm={confirmRemoveMatch}
          onClose={() => {
            if (deleting) return;
            setShowDeleteModal(false);
            setPendingIds([]);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(MatchesView);
